/* eslint-disable max-len */
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import CustomInputTextField from 'components/shared/custom_input_textfield';
import { useEffect, useState } from 'react';
import { Header } from 'components/home';
import { useSnackbar } from 'notistack';
import { TopNavBar } from 'components/top_nav_bar';
import { MenuProvider } from 'contexts/menu/menu_context';
import { useAuth } from 'contexts/auth/auth_context';
import { CicaafAPI } from 'api/cicaaf/cicaaf.api';
import { ROUTES } from 'routes/constants';
import { useNavigate } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SendIcon from '@mui/icons-material/Send';
import { ContainerForm } from 'components/shared/container_form';
import DocumentComponent from 'components/shared/document_component';
import { CicaafCollectionStatusEnum } from 'interfaces/cicaaf.interface';
import { mascaraCpfCnpj } from 'utils/format_cpf';

interface Collection {
  uuid: string;
  entity_name: string;
  identification: string;
  entity_type: 'COOPERATIVA' | 'ASSOCIAÇÃO' | 'AGROINDÚSTRIA';
  reference_year: number;
  status: CicaafCollectionStatusEnum;
  created_at: string;
  validated_at?: string;
  sent_to_state_at?: string;
  available_actions: {
    can_view: boolean;
    can_edit: boolean;
    can_validate_and_send: boolean;
  };
}

interface PaginatedResponse {
  data: Collection[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export const validateCollectionData = (collectionData: any): string[] => {
  const errors: string[] = [];
  const add = (msg: string) => { if (!errors.includes(msg)) errors.push(msg); };
  const str = (v: any) => (typeof v === 'string' ? v.trim() : v);
  const num = (v: any) => {
    if (v === null || v === undefined || v === '') return undefined;
    const n = Number(String(v).replace(',', '.'));
    return Number.isFinite(n) ? n : undefined;
  };
  const digits = (v: any) => (typeof v === 'string' ? v.replace(/\D/g, '') : v);

  try {

    const raw = collectionData?.dados ?? collectionData ?? {};

    const productsFromPC = Array.isArray(raw?.production_commercialization?.products)
      ? raw.production_commercialization.products
      : [];

    const productsFromCollection = Array.isArray(raw?.collectionProducts)
      ? raw.collectionProducts.map((cp: any) => ({
        commercialized_volume: num(cp?.commercialized_volume),
        commercialized_value: num(cp?.commercialized_value),
        measurement_unit: str(cp?.measurement_unit),
        product_uuid: str(cp?.product?.uuid),
        product_type: str(cp?.product?.type),
      }))
      : [];

    const products = (productsFromPC.length ? productsFromPC : productsFromCollection)
      .map((p: any) => ({
        commercialized_volume: num(p?.commercialized_volume),
        commercialized_value: num(p?.commercialized_value),
        measurement_unit: str(p?.measurement_unit),
        product_uuid: str(p?.product_uuid),
        product_type: str(p?.product_type),
      }));


    const processed_products = products.filter(
      (p: any) => (p?.product_type || '').toUpperCase().includes('PROCESSAD')
    );


    const city =
      str(raw?.city) ||
      str(raw?.entity_address?.municipality) ||
      str(raw?.institution?.address?.municipality);


    const geographic_coordinates = raw?.geographic_coordinates ?? {
      latitude: num(raw?.latitude),
      longitude: num(raw?.longitude),
    };


    const informant_data = raw?.informant_data ?? {
      informant_name: str(raw?.informant_name),
      informant_document: str(raw?.informant_document),
      informant_phone: str(raw?.informant_phone),
    };

    const vegetal_license =
      raw?.production_commercialization?.vegetal_license ?? raw?.vegetal_license;
    const animal_inspection =
      raw?.production_commercialization?.animal_inspection ?? raw?.animal_inspection;

    const dados = {
      entity_name: str(raw?.entity_name),
      identification: str(raw?.identification),
      entity_type: str(raw?.entity_type),
      city,
      cnpj_status: str(raw?.cnpj_status),
      location_type: str(raw?.location_type),
      belongs_to_association_cooperative: raw?.belongs_to_association_cooperative,

      production_commercialization: {
        products,
        ...(vegetal_license ? { vegetal_license } : {}),
        ...(animal_inspection ? { animal_inspection } : {}),
      },

      ...(processed_products.length
        ? { processed_production: { processed_products } }
        : {}),

      geographic_coordinates,
      informant_data,
    };


    if (!dados.entity_name) add('• Nome da entidade não informado');

    const idRaw = dados.identification;
    const idOnlyDigits = digits(idRaw || '');
    if (!idRaw) {
      add('• CPF/CNPJ não informado');
    } else if (idOnlyDigits.length !== 11 && idOnlyDigits.length !== 14) {
      add('• CPF/CNPJ em formato inválido');
    }

    if (!dados.entity_type) add('• Tipo de entidade não informado');
    if (!dados.city) add('• Cidade não informada');


    if (idOnlyDigits.length === 14 && !dados.cnpj_status) {
      add('• Situação cadastral do CNPJ não verificada');
    }


    if (!dados.location_type) add('• Tipo de localização da unidade não informado');


    const isAgro = (dados.entity_type || '').toString().toUpperCase().includes('AGROINDUSTRIA');
    if (isAgro) {
      if (dados.belongs_to_association_cooperative === null || dados.belongs_to_association_cooperative === undefined) {
        add('• Informação sobre vínculo com associação/cooperativa não preenchida');
      }
    }



    if (!dados.production_commercialization ||
      !Array.isArray(dados.production_commercialization.products) ||
      dados.production_commercialization.products.length === 0) {
      add('• Dados de produção e comercialização não preenchidos');
    } else {
      const hasValidProducts = dados.production_commercialization.products.some((product: any) => {
        const vol = num(product?.commercialized_volume);
        const val = num(product?.commercialized_value);
        const unit = str(product?.measurement_unit);
        const uuid = str(product?.product_uuid);
        return Number.isFinite(vol) && vol! > 0 &&
          Number.isFinite(val) && val! > 0 &&
          !!unit && !!uuid;
      });
      if (!hasValidProducts) {
        add('• Nenhum produto com dados completos de comercialização (volume, valor, unidade de medida)');
      }
    }

    if (dados.entity_type === 'AGROINDÚSTRIA') {
      const list = dados?.processed_production?.processed_products;
      if (!Array.isArray(list) || list.length === 0) {
        add('• Dados de produção processada são obrigatórios para agroindústrias');
      } else {
        const ok = list.some((product: any) => {
          const vol = num(product?.commercialized_volume);
          const val = num(product?.commercialized_value);
          const unit = str(product?.measurement_unit);
          const uuid = str(product?.product_uuid);
          return Number.isFinite(vol) && vol! > 0 &&
            Number.isFinite(val) && val! > 0 &&
            !!unit && !!uuid;
        });
        if (!ok) add('• Nenhum produto processado com dados completos de comercialização');
      }
    }

    if (dados.production_commercialization?.vegetal_license?.has_sanitary_license === true) {
      const v = dados.production_commercialization.vegetal_license;
      if (!v.license_number || !v.issuing_agency || !v.issue_date) {
        add('• Dados da licença sanitária vegetal incompletos');
      }
    }

    if (dados.production_commercialization?.animal_inspection?.has_inspection_register === true) {
      const ai = dados.production_commercialization.animal_inspection;
      if (!Array.isArray(ai?.inspection_seals) || ai.inspection_seals.length === 0) {
        add('• Selos de inspeção animal não informados');
      } else {
        const sealsOk = ai.inspection_seals.every(
          (seal: any) => str(seal?.inspection_seal) && str(seal?.register_number) && str(seal?.issue_date)
        );
        if (!sealsOk) add('• Dados dos selos de inspeção animal incompletos');
      }
    }

    const lat = num(dados?.geographic_coordinates?.latitude);
    const lon = num(dados?.geographic_coordinates?.longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
      add('• Coordenadas geográficas não informadas ou incompletas');
    } else if (lat! < -90 || lat! > 90 || lon! < -180 || lon! > 180) {
      add('• Coordenadas geográficas inválidas (fora da faixa)');
    }

    if (!dados.informant_data ||
      !str(dados.informant_data.informant_name) ||
      !str(dados.informant_data.informant_document) ||
      !str(dados.informant_data.informant_phone)) {
      add('• Dados do informante incompletos (nome, documento, telefone)');
    }

  } catch (error) {
    console.error('Erro durante validação dos dados:', error);
    add('• Erro ao validar estrutura dos dados da coleta');
  }

  return errors;
};

export const getStatusLabel = (status: CicaafCollectionStatusEnum) => {
  switch (status) {
    case CicaafCollectionStatusEnum.COLLECTING_INCOMPLETE:
      return 'Em coleta';
    case CicaafCollectionStatusEnum.SYNCHRONIZED:
      return 'Sincronizado';
    case CicaafCollectionStatusEnum.VALIDATED_BY_MUNICIPALITY:
      return 'Validado pelo município';
    case CicaafCollectionStatusEnum.SENT_TO_STATE:
      return 'Enviado ao Estado';
    case CicaafCollectionStatusEnum.RETURNED_BY_STATE:
      return 'Devolvido pelo Estado';
    default:
      return status as unknown as string;
  }
};

export const getStatusColor = (status: CicaafCollectionStatusEnum) => {
  switch (status) {
    case CicaafCollectionStatusEnum.COLLECTING_INCOMPLETE:
      return { backgroundColor: '#FFF3E0', color: '#E65100' };
    case CicaafCollectionStatusEnum.SYNCHRONIZED:
      return { backgroundColor: '#E3F2FD', color: '#1565C0' };
    case CicaafCollectionStatusEnum.VALIDATED_BY_MUNICIPALITY:
      return { backgroundColor: '#E8F5E9', color: '#2E7D32' };
    case CicaafCollectionStatusEnum.SENT_TO_STATE:
      return { backgroundColor: '#F3E5F5', color: '#7B1FA2' };
    case CicaafCollectionStatusEnum.RETURNED_BY_STATE:
      return { backgroundColor: '#FFEBEE', color: '#C62828' };
    default:
      return { backgroundColor: '#EEEEEE', color: '#424242' };
  }
};

export const canUserValidateAndSend = (user: any) => {
  const permissionName = user?.permission?.name?.toUpperCase?.() || '';
  return permissionName.includes('GERENTE MUNICIPAL') || permissionName.includes('ADMINISTRADOR');
};

export const buildSendPayload = (formulario: { upload_estatuto: string | null; upload_ata: string | null; observations: string }) => ({
  statute_document_file_uuid: formulario.upload_estatuto,
  election_act_document_file_uuid: formulario.upload_ata,
  observations: formulario.observations,
});

export const formatEntityTypeLabel = (entityType?: string) => {
  if (entityType === 'COOPERATIVA') return 'Cooperativa';
  if (entityType === 'ASSOCIAÇÃO') return 'Associação';
  if (entityType === 'AGROINDÚSTRIA') return 'Agroindústria';
  return entityType ?? '';
};

export const shouldShowEditButton = (status: CicaafCollectionStatusEnum) => (
  status === CicaafCollectionStatusEnum.SYNCHRONIZED ||
  status === CicaafCollectionStatusEnum.RETURNED_BY_STATE ||
  // keep parity with component logic
  status === (CicaafCollectionStatusEnum as any).COLLECTING_WEB
);

export const shouldShowValidateAndSendButton = (status: CicaafCollectionStatusEnum, canValidateFlag: boolean) => (
  canValidateFlag && (status === CicaafCollectionStatusEnum.RETURNED_BY_STATE || status === CicaafCollectionStatusEnum.SYNCHRONIZED)
);

export const formatIdentification = (ident?: string) => mascaraCpfCnpj(ident ?? '');

export const getActionVisibility = (
  status: CicaafCollectionStatusEnum,
  canValidateFlag: boolean,
) => ({
  canView: true,
  canEdit:
    status === CicaafCollectionStatusEnum.SYNCHRONIZED ||
    status === CicaafCollectionStatusEnum.RETURNED_BY_STATE ||
    status === (CicaafCollectionStatusEnum as any).COLLECTING_WEB,
  canValidateAndSend:
    canValidateFlag &&
    (status === CicaafCollectionStatusEnum.RETURNED_BY_STATE ||
      status === CicaafCollectionStatusEnum.SYNCHRONIZED),
});

export const formatRowData = (collection: Collection) => ({
  name: collection.entity_name,
  idFormatted: formatIdentification(collection.identification),
  entityTypeLabel: formatEntityTypeLabel(collection.entity_type),
  referenceYear: collection.reference_year,
  statusLabel: getStatusLabel(collection.status),
  statusColor: getStatusColor(collection.status),
});

export const getActionButtonLabels = () => ({
  view: 'Visualizar',
  edit: 'Editar',
  validateAndSend: 'Validar e Enviar ao Estado',
});

export const getDialogConfirmLabel = (validating: boolean) => (
  validating ? 'Validando dados...' : 'Confirmar Envio'
);

export const isConfirmSendDisabled = (
  formulario: { upload_estatuto: string | null; upload_ata: string | null },
  validating: boolean,
) => !formulario.upload_estatuto || !formulario.upload_ata || validating;

export const buildValidationErrorMessage = (errors: string[]) => (
  `A coleta possui dados incompletos que impedem o envio:\n\n${errors.join('\n')}\n\nPor favor, edite a coleta e preencha os dados obrigatórios antes de tentar enviar novamente.`
);


export const GerenciamentoColetasCicaaf: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [collections, setCollections] = useState<Collection[]>([]);
  const [loading, setLoading] = useState(true);
  const [validating, setValidating] = useState(false);
  const [openSendDialog, setOpenSendDialog] = useState(false);
  const [selectedCollection, setSelectedCollection] =
    useState<Collection | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 0,
    limit: 20,
    totalPages: 1,
  });

  const [formulario, setFormulario] = useState({
    upload_estatuto: null,
    upload_ata: null,
    observations: '',
  });

  const [filters, setFilters] = useState({
    entity_name: '',
    identification: '',
    entity_type: 'TODAS',
    reference_year: '',
  });

  const setPartialForm = (campo: string, valor: any) => {
    setFormulario((prev) => ({
      ...prev,
      [campo]: valor,
    }));
  };

  const fetchCollections = async (filterParams?: typeof filters) => {
    try {
      setLoading(true);
      const params = filterParams || filters;
      const apiParams: any = {};
      
      if (params.entity_name.trim()) {
        apiParams.entity_name = params.entity_name.trim();
      }
      if (params.identification.trim()) {
        apiParams.identification = params.identification.replace(/\D/g, '');
      }
      if (params.entity_type && params.entity_type !== 'TODAS') {
        apiParams.entity_type = params.entity_type;
      }
      if (params.reference_year.trim() && params.reference_year.trim().length === 4) {
        const year = parseInt(params.reference_year);
        if (!isNaN(year)) {
          apiParams.reference_year = year;
        }
      }

      const response = await CicaafAPI.listCollections(apiParams);

      if (response.Status === 'OK' && response.dados) {
        const data = response.dados as PaginatedResponse;
        setCollections(data.data);
        setPagination(data.pagination);
      } else {
        console.error('Erro ao buscar coletas:', response.Mensagem);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching collections:', error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCollections();
  }, []);

  const handleSearch = () => {
    fetchCollections();
  };

  const handleClearFilters = () => {
    setFilters({
      entity_name: '',
      identification: '',
      entity_type: 'TODAS',
      reference_year: '',
    });
    fetchCollections({
      entity_name: '',
      identification: '',
      entity_type: 'TODAS',
      reference_year: '',
    });
  };

  const formatIdentificationInput = (value: string): string => {
    const cleaned = value.replace(/\D/g, '');
    
    if (cleaned.length <= 11) {
      if (cleaned.length <= 3) return cleaned;
      if (cleaned.length <= 6) return cleaned.replace(/^(\d{3})(\d+)$/, '$1.$2');
      if (cleaned.length <= 9) return cleaned.replace(/^(\d{3})(\d{3})(\d+)$/, '$1.$2.$3');
      return cleaned.replace(/^(\d{3})(\d{3})(\d{3})(\d+)$/, '$1.$2.$3-$4');
    }
    
    if (cleaned.length <= 14) {
      if (cleaned.length <= 2) return cleaned;
      if (cleaned.length <= 5) return cleaned.replace(/^(\d{2})(\d+)$/, '$1.$2');
      if (cleaned.length <= 8) return cleaned.replace(/^(\d{2})(\d{3})(\d+)$/, '$1.$2.$3');
      if (cleaned.length <= 12) return cleaned.replace(/^(\d{2})(\d{3})(\d{3})(\d+)$/, '$1.$2.$3/$4');
      return cleaned.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d+)$/, '$1.$2.$3/$4-$5');
    }
    
    return cleaned.slice(0, 14).replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
  };

  const handleView = (collectionUuid: string) => {
    navigate(ROUTES.GERENCIAMENTO_COLETAS_CICAAF_VISUALIZAR.replace(':uuid', collectionUuid));
  };


  const handleEdit = (collectionUuid: string) => {
    navigate(ROUTES.GERENCIAMENTO_COLETAS_CICAAF_EDITAR.replace(':uuid', collectionUuid));
  };

  const canValidateAndSend = canUserValidateAndSend(user);


  // use exported helpers getStatusLabel/getStatusColor

  const handleSendToState = (collection: Collection) => {
    setSelectedCollection(collection);
    setOpenSendDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenSendDialog(false);
    setSelectedCollection(null);
    setError(null);
    setFormulario({
      upload_estatuto: null,
      upload_ata: null,
      observations: '',
    });
  };

  const confirmSendToState = async () => {
    // 1. Validação dos documentos obrigatórios
    if (
      !selectedCollection ||
      !formulario.upload_estatuto ||
      !formulario.upload_ata
    ) {
      setError(
        'É obrigatório anexar o Estatuto Social e a Ata de Eleição da Diretoria.',
      );
      return;
    }

    // 2. Validação dos dados da coleta antes do envio
    setValidating(true);
    setError(null);

    try {

      const collectionData = await CicaafAPI.getDataByID(selectedCollection.uuid);
      
      const raw = collectionData?.dados ?? collectionData ?? {};
      if (!raw.collectionProducts || raw.collectionProducts.length === 0) {
        try {
          const pcData = await CicaafAPI.getProductionComercialization(selectedCollection.uuid);
          if (pcData?.dados?.products && pcData.dados.products.length > 0) {
            raw.production_commercialization = pcData.dados;
          }
        } catch (error: any) {
          if (error?.response?.status !== 403) {
            console.warn('Erro ao buscar dados de produção e comercialização:', error);
          }
        }
      }

      const validationErrors = validateCollectionData(collectionData);

      if (validationErrors.length > 0) {
        setValidating(false);
        setError(
          `A coleta possui dados incompletos que impedem o envio:\n\n${validationErrors.join('\n')}\n\nPor favor, edite a coleta e preencha os dados obrigatórios antes de tentar enviar novamente.`
        );
        return;
      }

    } catch (validationError) {
      console.error('Erro ao validar dados da coleta:', validationError);
      setValidating(false);
      setError('Erro ao validar os dados da coleta. Tente novamente.');
      return;
    }

    const payload = {
      statute_document_file_uuid: formulario.upload_estatuto,
      election_act_document_file_uuid: formulario.upload_ata,
      observations: formulario.observations,
    };

    try {
      const response = await CicaafAPI.validateAndSendCollection(
        selectedCollection.uuid,
        payload,
      );

      if (response.Status === 'OK' && response.dados) {
        const { status, sent_to_state_at } = response.dados;

        setCollections((prev) =>
          prev.map((c) =>
            c.uuid === selectedCollection.uuid
              ? {
                ...c,
                status: status as CicaafCollectionStatusEnum,
                sent_to_state_at: sent_to_state_at,
                available_actions: {
                  ...c.available_actions,
                  can_validate_and_send: false,
                  can_edit: false,
                },
              }
              : c,
          ),
        );

        enqueueSnackbar(
          'Validação e envio ao estado confirmados com sucesso!',
          {
            variant: 'success',
          },
        );

        handleCloseDialog();
      } else {
        const errorMessage = response.Mensagem || 'Ocorreu um erro ao enviar a coleta.';
        console.error('Erro na resposta da API:', response);
        setError(errorMessage);
      }
    } catch (err) {
      console.error('Erro ao conectar com a API:', err);

      // Tratamento mais específico de erros
      if (err instanceof Error) {
        if (err.message.includes('400')) {
          setError('Dados inválidos ou incompletos. Verifique se todos os campos obrigatórios foram preenchidos.');
        } else if (err.message.includes('422')) {
          setError('A coleta não atende aos requisitos para envio ao Estado. Verifique se todos os dados estão completos e corretos.');
        } else {
          setError('Não foi possível conectar com o servidor. Tente novamente mais tarde.');
        }
      } else {
        setError('Erro inesperado. Tente novamente mais tarde.');
      }
    } finally {
      setValidating(false);
    }
  };

  return (
    <MenuProvider>
      <TopNavBar />
      <Header
        title={`GERENCIAR COLETAS DAS ENTIDADES`}
        showBackButton={true}
      />
      <Box
        width='97%'
        display='flex'
        flexDirection='column'
        justifyContent='center'
        alignItems='center'
        margin='2rem'
      >
        <ContainerForm
          title='Coletas do Município'
          subtitle={`Gerencie as coletas do seu município para o ano de ${new Date().getFullYear()}`}
          width='100%'
        >
          <Paper elevation={1} sx={{ p: 3, mb: 3, backgroundColor: 'white' }}>
            <Typography variant="h6" gutterBottom sx={{ mb: 2, color: 'text.primary' }}>
              Filtros de Pesquisa
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <Box sx={{ minWidth: 250, width: '100%', display: 'flex', gap: 2 }}>
                <CustomInputTextField
                  value={filters.entity_name}
                  onChange={(e) => setFilters({ ...filters, entity_name: e.target.value })}
                  label="Nome da Entidade"
                  type="text"
                  inputProps={{}}
                  error={{}}
                  helperText={{}}
                  fullWidth
                  margin="dense"
                  required={false}
                />
                <CustomInputTextField
                  value={filters.identification}
                  onChange={(e) => {
                    const formatted = formatIdentificationInput(e.target.value);
                    setFilters({ ...filters, identification: formatted });
                  }}
                  label="CNPJ/CPF"
                  type="text"
                  inputProps={{ maxLength: 18 }}
                  error={{}}
                  helperText={{}}
                  fullWidth
                  margin="dense"
                  required={false}
                />
                <FormControl fullWidth margin="dense">
                  <InputLabel shrink>Tipo de Entidade</InputLabel>
                  <Select
                    value={filters.entity_type}
                    onChange={(e) => setFilters({ ...filters, entity_type: e.target.value })}
                    label="Tipo de Entidade"
                    notched
                  >
                    <MenuItem value="TODAS">Todas</MenuItem>
                    <MenuItem value="COOPERATIVA">Cooperativa</MenuItem>
                    <MenuItem value="ASSOCIAÇÃO">Associação</MenuItem>
                    <MenuItem value="AGROINDÚSTRIA">Agroindústria</MenuItem>
                  </Select>
                </FormControl>
                <CustomInputTextField
                  value={filters.reference_year}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 4);
                    setFilters({ ...filters, reference_year: value });
                  }}
                  label="Ano Referência"
                  type="text"
                  placeholder="Digite 4 dígitos (ex: 2026)"
                  inputProps={{ maxLength: 4 }}
                  error={{}}
                  helperText={{}}
                  fullWidth
                  margin="dense"
                  required={false}
                />
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSearch}
                    sx={{
                      minWidth: 120
                    }}
                  >
                    Pesquisar
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={handleClearFilters}
                    sx={{
                      minWidth: 120
                    }}
                  >
                    Limpar
                  </Button>
                </Box>
              </Box>
            </Box>
          </Paper>
          {loading ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                padding: '40px',
              }}
            >
              <Typography>Carregando coletas...</Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} sx={{ marginBottom: '40px' }}>
              <Table>
                <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>
                      Nome da Entidade
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>CNPJ/CPF</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>
                      Tipo de Entidade
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>
                      Ano Referência
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>
                      Status da Coleta
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {collections.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ padding: '40px' }}>
                        <Typography variant="body1" color="text.secondary">
                          Nenhum Registro Encontrado
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    collections.map((collection) => (
                      <TableRow key={collection.uuid}>
                        <TableCell>{collection.entity_name}</TableCell>
                        <TableCell>
                          {mascaraCpfCnpj(collection.identification)}
                        </TableCell>
                        <TableCell>
                          {collection.entity_type === 'COOPERATIVA' &&
                            'Cooperativa'}
                          {collection.entity_type === 'ASSOCIAÇÃO' &&
                            'Associação'}
                          {collection.entity_type === 'AGROINDÚSTRIA' &&
                            'Agroindústria'}
                        </TableCell>
                        <TableCell>{collection.reference_year}</TableCell>
                        <TableCell>
                          <Box
                            sx={{
                              display: 'inline-block',
                              padding: '4px 8px',
                              borderRadius: '4px',
                              ...getStatusColor(collection.status),
                              fontWeight: 'bold',
                              fontSize: '0.75rem',
                            }}
                          >
                            {getStatusLabel(collection.status)}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: '8px' }}>
                            <Button
                              variant='outlined'
                              size='small'
                              startIcon={<VisibilityIcon />}
                              onClick={() => handleView(collection.uuid)}
                              sx={{ textTransform: 'none' }}
                            >
                              Visualizar
                            </Button>
                            {collection.available_actions?.can_edit !== false && (
                                <Button
                                  variant='outlined'
                                  size='small'
                                  startIcon={<EditIcon />}
                                  onClick={() => handleEdit(collection.uuid)}
                                  sx={{ textTransform: 'none' }}
                                >
                                  Editar
                                </Button>
                              )}

                            {canValidateAndSend &&
                              (collection.status === CicaafCollectionStatusEnum.RETURNED_BY_STATE ||
                                collection.status === CicaafCollectionStatusEnum.SYNCHRONIZED) && (
                                <Button
                                  variant='contained'
                                  size='small'
                                  startIcon={<SendIcon />}
                                  onClick={() => handleSendToState(collection)}
                                  sx={{ textTransform: 'none' }}
                                >
                                  Validar e Enviar ao Estado
                                </Button>
                              )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <Dialog
            open={openSendDialog}
            onClose={handleCloseDialog}
            maxWidth='sm'
            fullWidth
          >
            <DialogTitle>
              <Typography variant='h6' fontWeight='bold'>
                Confirmar Envio ao Estado
              </Typography>
            </DialogTitle>
            <DialogContent>
              <Box
                sx={{
                  backgroundColor: '#FFF8E1',
                  border: '1px solid #FFD54F',
                  borderRadius: 1,
                  padding: 2,
                  mt: 1,
                }}
              >
                <Typography
                  variant='subtitle1'
                  fontWeight='bold'
                  color='warning.dark'
                >
                  Atenção
                </Typography>
                <Typography variant='body2' mt={0.5}>
                  Após o envio, a coleta não poderá mais ser editada.
                  Certifique-se de que todas as informações e documentos estão
                  corretos.
                </Typography>
              </Box>

              <Box mt={3}>
                <DocumentComponent
                  uuid={formulario.upload_estatuto ?? null}
                  setUuid={(uuid) => setPartialForm('upload_estatuto', uuid)}
                  podeSubstituir={true}
                  title='Upload do Estatuto Social *'
                  subtitle='Anexe o documento que comprova o estatuto vigente da entidade.'
                />
              </Box>

              <Box mt={3}>
                <DocumentComponent
                  uuid={formulario.upload_ata ?? null}
                  setUuid={(uuid) => setPartialForm('upload_ata', uuid)}
                  podeSubstituir={true}
                  title='Upload da Ata de Eleição da Diretoria *'
                  subtitle='Anexe o documento da eleição da atual diretoria.'
                />
              </Box>

              <Box mt={3}>
                <TextField
                  fullWidth
                  label='Observações (Opcional)'
                  multiline
                  rows={3}
                  value={formulario.observations}
                  onChange={(e) =>
                    setPartialForm(
                      'observations',
                      e.target.value.toLocaleUpperCase(),
                    )
                  }
                  variant='outlined'
                  placeholder='Insira aqui qualquer observação relevante sobre o envio...'
                />
              </Box>

              {validating && (
                <Box
                  sx={{
                    mt: 2,
                    p: 2,
                    backgroundColor: '#e3f2fd',
                    border: '1px solid #2196f3',
                    borderRadius: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}
                >
                  <Typography
                    color='primary'
                    sx={{ fontWeight: 'bold' }}
                  >
                    🔍 Validando dados da coleta...
                  </Typography>
                </Box>
              )}

              {error && (
                <Box
                  sx={{
                    mt: 2,
                    p: 2,
                    backgroundColor: '#ffebee',
                    border: '1px solid #f44336',
                    borderRadius: '4px'
                  }}
                >
                  <Typography
                    color='error'
                    sx={{ fontWeight: 'bold', mb: 1 }}
                  >
                    ⚠️ Erro na Validação
                  </Typography>
                  <Typography
                    color='error'
                    sx={{ whiteSpace: 'pre-line', lineHeight: 1.5 }}
                  >
                    {error}
                  </Typography>
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button
                onClick={handleCloseDialog}
                variant='outlined'
                disabled={validating}
              >
                Cancelar
              </Button>
              <Button
                onClick={confirmSendToState}
                variant='contained'
                color='primary'
                disabled={!formulario.upload_estatuto || !formulario.upload_ata || validating}
              >
                {validating ? 'Validando dados...' : 'Confirmar Envio'}
              </Button>
            </DialogActions>
          </Dialog>
        </ContainerForm>
      </Box>
    </MenuProvider>
  );
};
